<template>
  <Modal
    :open="open"
    :hide-footer="true"
    customWidth="lg:w-[960px]"
    customHeight="h-[85%] lg:h-auto lg:max-h-[85vh]"
    @close-modal="$emit('close')"
  >
    <template #modal-header>
      <div>
        <p class="flex items-center gap-2 font-inter text-md font-medium text-success">
          <Icon name="Search" class="h-4 w-4" :stroke-width="2" />
          {{ labels.eyebrow }}
        </p>
        <p class="text-2xl font-medium leading-tight text-strongest mr-2 md:text-3xl">
          {{ labels.title }}
        </p>
      </div>
    </template>

    <template #modal-body>
      <!-- Nothing to show: either nothing is scheduled yet (an investment that
      has settled but has not reached its first payment date) or the requested
      payment date has no distributions behind it. -->
      <div v-if="!data.hasEarnings" class="flex flex-col items-center gap-2 px-6 py-12 text-center">
        <Icon name="CalendarClock" class="h-8 w-8 text-light" :stroke-width="1.5" />
        <p class="font-inter text-lg font-medium text-strongest">{{ labels.emptyTitle }}</p>
        <p class="font-inter text-sm font-medium text-strong opacity-60">
          {{ labels.emptyBody }}
        </p>
      </div>

      <div v-else class="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
        <!-- Summary: 3 cards on desktop -->
        <div class="hidden gap-2 md:grid md:grid-cols-3">
          <div class="rounded-2xl bg-container-bg p-5">
            <p class="text-sm font-medium text-strong opacity-60">{{ labels.total }}</p>
            <p class="text-3xl font-medium text-success">
              {{ data.totalEarning }}
            </p>
            <p class="mt-1 text-sm font-medium text-strong">{{ labels.dateLine }}</p>
          </div>
          <div class="rounded-2xl bg-container-bg p-5">
            <p class="text-sm font-medium text-strong opacity-60">{{ labels.payment }}</p>
            <p class="text-3xl font-medium text-strongest">
              {{ data.monthlyPayment }}
            </p>
          </div>
          <div class="rounded-2xl bg-container-bg p-5">
            <p class="text-sm font-medium text-strong opacity-60">{{ labels.accrued }}</p>
            <p class="text-3xl font-medium text-strongest">
              {{ data.accruedInterest }}
            </p>
          </div>
        </div>

        <!-- Summary: stacked rows on mobile -->
        <div class="space-y-2 md:hidden">
          <div class="flex items-start justify-between">
            <span class="text-lg/6 font-inter font-medium text-strong">
              {{ labels.total }}<br />{{ labels.dateLineLower }}
            </span>
            <span class="text-xl font-inter text-success">{{ data.totalEarning }}</span>
          </div>
          <div class="flex items-center justify-between gap-4">
            <span class="text-lg font-inter font-medium text-strong">{{ labels.payment }}</span>
            <span class="text-xl font-inter text-strongest">{{ data.monthlyPayment }}</span>
          </div>
          <div class="flex items-center justify-between gap-4">
            <span class="text-lg font-inter font-medium text-strong">{{ labels.accrued }}</span>
            <span class="text-xl font-inter text-strongest">{{ data.accruedInterest }}</span>
          </div>
        </div>

        <!-- Table (desktop) -->
        <div class="hidden md:block">
          <div
            class="grid grid-cols-5 gap-4 px-5 py-2 text-sm font-semibold text-strong opacity-60"
          >
            <span>Investment ID</span>
            <span>Payment sent to</span>
            <span>Monthly payment</span>
            <span>Accrued interest</span>
            <span>Type</span>
          </div>
          <div class="space-y-2">
            <div
              v-for="row in data.rows"
              :key="row.investmentId"
              class="grid grid-cols-5 items-center gap-4 rounded-2xl bg-container-bg px-5 py-4"
            >
              <button
                type="button"
                class="flex items-center gap-1 text-left font-medium text-info hover:underline"
                @click="goToInvestment(row.investmentId)"
              >
                {{ row.investmentIdShort }}
                <Icon name="ArrowUpRight" class="h-4 w-4" :stroke-width="2" />
              </button>
              <span :class="row.paymentSentTo === '-' ? 'text-success' : 'text-strongest'">
                {{ row.paymentSentTo }}
              </span>
              <span :class="row.monthlyPayment === '-' ? 'text-success' : 'text-strongest'">
                {{ row.monthlyPayment }}
              </span>
              <span :class="row.accruedInterest === '-' ? 'text-success' : 'text-strongest'">
                {{ row.accruedInterest }}
              </span>
              <span class="text-strongest">{{ row.type }}</span>
            </div>
          </div>
        </div>

        <!-- Cards (mobile) -->
        <div class="space-y-3 md:hidden">
          <div v-for="row in data.rows" :key="row.investmentId" class="rounded-2xl bg-container-bg p-4">
            <p class="text-md font-inter font-semibold text-strong">
              {{ row.monthlyPayment !== "-" ? "Monthly payment" : "Accrued interest" }}
            </p>
            <p class="text-xl font-inter text-strongest">
              {{ row.monthlyPayment !== "-" ? row.monthlyPayment : row.accruedInterest }}
            </p>
            <!-- Two columns so every value starts on the same left edge, per design,
            rather than each row being pushed flush-right independently. -->
            <div class="mt-4 grid grid-cols-[auto_1fr] gap-x-14 gap-y-1">
              <span class="text-md font-inter font-semibold text-strong">Investment ID</span>
              <button
                type="button"
                class="flex items-center gap-1 text-md font-semibold font-inter text-info hover:underline justify-self-start"
                @click="goToInvestment(row.investmentId)"
              >
                {{ row.investmentIdShort }}
                <Icon name="ArrowUpRight" class="h-4 w-4" :stroke-width="2" />
              </button>

              <span class="text-md font-inter font-semibold text-strong">Type</span>
              <span class="text-md font-inter text-strongest">{{ row.type }}</span>

              <span class="text-md font-inter font-semibold text-strong">Payment sent to</span>
              <span class="text-md font-inter text-strongest">{{ row.paymentSentTo }}</span>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { computed } from "vue";
import { useRouter } from "vue-router";
import { Modal, Icon } from "@/components";
import { useInvestmentStore } from "@/stores";

const props = defineProps({
  open: { type: Boolean, required: true },
  // Which payment to break down. Null (the default) = the next scheduled
  // payment, which is what the dashboard hero and the Portfolio "Next monthly
  // earning" card open. A distribution date = a payment already received,
  // opened from a row of the earnings history.
  distributionDate: { type: String, default: null },
  // The payment-type pill in force on the row that was opened, so the totals
  // here match the row the investor clicked.
  // "all" | "simple" (monthly payments) | "compounded" (accrued interest).
  filter: { type: String, default: "all" },
});

const emit = defineEmits(["close"]);

const router = useRouter();
const investmentStore = useInvestmentStore();

const data = computed(() =>
  investmentStore.getEarningsBreakdown(props.distributionDate, props.filter)
);

// A scheduled payment is described in the future tense ("Next monthly
// earning"), one already received in the past tense.
const labels = computed(() => {
  const { isUpcoming, hasEarnings, monthLabel, dayLabel } = data.value;

  if (isUpcoming) {
    return {
      eyebrow: "Next monthly earning",
      title: hasEarnings
        ? `Breakdown of upcoming earnings for ${monthLabel}`
        : "No upcoming earnings scheduled",
      total: "Next monthly earning",
      payment: "Next monthly payment",
      accrued: "Next accrued interest",
      dateLine: `On ${dayLabel}`,
      dateLineLower: `on ${dayLabel}`,
      emptyTitle: "No payments scheduled",
      emptyBody: "Your next earnings will appear here once a payment date has been set.",
    };
  }

  return {
    eyebrow: "Monthly earning",
    title: hasEarnings ? `Breakdown of earnings for ${monthLabel}` : "No earnings for this payment",
    total: "Total earning",
    payment: "Monthly payment",
    accrued: "Accrued interest",
    dateLine: `Paid on ${dayLabel}`,
    dateLineLower: `paid on ${dayLabel}`,
    emptyTitle: "Nothing to break down",
    emptyBody: "No earnings were recorded for this payment date.",
  };
});

function goToInvestment(recordId) {
  emit("close");
  router.push(`/investment-details?recordId=${recordId}`);
}
</script>
